import React, { useState, useRef } from 'react';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from '../hooks/use-toast';
import { Car, ChevronRight, Calendar as CalendarIcon, Clock, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import ReCAPTCHA from "react-google-recaptcha";
import { useCountries } from '../hooks/useCountries';

const TestDrive = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = translations[language].testDrive;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  const { countries } = useCountries();

  // Exact models requested by user
  const carModels = {
    suv: [
      { id: 'bj30', name: 'BJ30 Hybrid', image: '/assets/images/models/bj30.png' },
      { id: 'bj40honor', name: 'BJ40 HONOR', image: '/assets/images/models/bj40-honor.png' },
      { id: 'bj40pro', name: 'BJ40 PRO', image: '/assets/images/models/bj40-pro.png' },
    ],
    sedan: [
      { id: 'u5plus', name: 'U5 PLUS', image: '/assets/images/models/u5-plus.png' },
    ]
  };

  const schema = yup.object().shape({
    title: yup.string().required(language === 'ar' ? "اللقب مطلوب" : "Title is required"),
    firstName: yup.string().required(language === 'ar' ? "الاسم الأول مطلوب" : "First Name is required"),
    lastName: yup.string().required(language === 'ar' ? "اسم العائلة مطلوب" : "Last Name is required"),
    email: yup.string().email(language === 'ar' ? "بريد إلكتروني غير صالح" : "Invalid email").required(language === 'ar' ? "البريد الإلكتروني مطلوب" : "Email is required"),
    countryCode: yup.string().required(),
    phone: yup.string().required(language === 'ar' ? "رقم الهاتف مطلوب" : "Phone is required"),
    location: yup.string().required(language === 'ar' ? "الموقع مطلوب" : "Location is required"),
    date: yup.date().required(language === 'ar' ? "التاريخ مطلوب" : "Date is required"),
    time: yup.string().required(language === 'ar' ? "الوقت مطلوب" : "Time is required"),
    message: yup.string(),
    model_id: yup.string().required(language === 'ar' ? "يرجى اختيار السيارة" : "Please select a car"),
    privacyAgreement: yup.boolean().oneOf([true], language === 'ar' ? "يرجى الموافقة على الخصوصية" : "You must agree to the privacy statement"),
  });

  const { register, handleSubmit, control, formState: { errors }, reset, watch, setValue, trigger, setError } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      countryCode: '+968',
      phone: '',
      location: '',
      time: '',
      message: '',
      privacyAgreement: false,
      model_id: ''
    }
  });

  const selectedCar = watch("model_id");

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const onSubmit = async (data) => {
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (recaptchaSiteKey && !captchaToken) {
      toast({
        title: language === 'ar' ? "خطأ في التحقق" : "Validation Error",
        description: language === 'ar' ? "يرجى إكمال التحقق من ReCAPTCHA." : "Please complete the ReCAPTCHA verification.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('firstName', data.firstName);
      formData.append('lastName', data.lastName);
      formData.append('email', data.email);
      // Check if phone contains + or just digits. we combine them
      formData.append('phone', `${data.countryCode}${data.phone}`);
      formData.append('location', data.location);

      const d = new Date(data.date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      formData.append('date', `${day}-${month}-${year}`);

      formData.append('time', data.time);
      formData.append('message', data.message || "");
      
      // Get model name from ID
      const allModels = [...carModels.suv, ...carModels.sedan];
      const selectedModel = allModels.find(m => m.id === data.model_id);
      const modelName = selectedModel ? selectedModel.name : data.model_id;

      formData.append('model_id', data.model_id);
      formData.append('model_name', modelName);
      formData.append('recaptcha_token', captchaToken);

      const response = await axios.post('https://admin.baicoman.com/api/test-drive-leads', formData);

      if (response.status === 200 || response.status === 201) {
        toast({
          title: language === 'ar' ? "تم التسجيل بنجاح" : "Booking Confirmed",
          description: language === 'ar' ? "سيتصل بك موظفنا قريبًا لتأكيد موعدك." : "Our concierge will contact you shortly to confirm your appointment.",
          className: "bg-green-600 text-white border-none"
        });
        reset();
        setValue("nationality", ""); // Reset nationality explicitly
        if (recaptchaSiteKey && recaptchaRef.current) {
          recaptchaRef.current.reset();
        }
        setCaptchaToken(null);
      }
    } catch (error) {
      console.error(error);
      if (error.response && error.response.data && error.response.data.errors) {
        const serverErrors = error.response.data.errors;
        Object.keys(serverErrors).forEach((field) => {
          setError(field, {
            type: "server",
            message: serverErrors[field][0],
          });
        });
        toast({
          title: language === 'ar' ? "خطأ في التحقق" : "Validation Error",
          description: language === 'ar' ? "يرجى التحقق من الحقول المطلوبة." : "Please check the form for errors.",
          variant: "destructive",
        });
      } else {
        toast({
          title: language === 'ar' ? "خطأ" : "Error",
          description: language === 'ar' ? "حدث خطأ ما. يرجى المحاولة مرة أخرى." : "Something went wrong. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCarSelect = (id) => {
    setValue("model_id", id);
    trigger("model_id");
  };

  return (
    <div className="min-h-screen bg-white mt-20 lg:mt-[120px]">
      <SEO
        title="Book a Test Drive | BAIC Oman - Experience the Drive"
        description="Book a test drive with BAIC Oman today. Experience the performance of our SUVs and Sedans in Oman."
        canonicalUrl="https://baicoman.com/test-drive"
      />
      <h1 className="sr-only">Book A Test Drive with BAIC Oman</h1>
      {/* Premium Hero */}
      <div className="relative h-[60vh] w-full bg-black overflow-hidden">
        <motion.img
          src="/assets/images/test-drive/test-drive.png"
          alt="Test Drive Experience"
          className="w-full h-full object-cover opacity-80"
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.8 }}
          transition={{ duration: 1.5 }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end px-4 md:px-20 pb-20 max-w-7xl mx-auto">
          <motion.h5
            className="text-red-600 font-bold tracking-[0.2em] mb-4 uppercase text-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            {t.hero.subtitle}
          </motion.h5>
          <motion.h2
            className="text-5xl md:text-8xl font-black text-white uppercase tracking-tighter mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            {t.hero.title}
          </motion.h2>
          <motion.p
            className="text-gray-300 text-lg md:text-xl max-w-xl font-light leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            {t.hero.desc}
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-24 relative">

        {/* Animated Side Line */}
        <motion.div
          className="absolute left-4 md:left-[59px] top-24 bottom-24 w-[2px] bg-gradient-to-b from-red-600 via-gray-200 to-gray-200 hidden md:block"
          initial={{ height: 0 }}
          whileInView={{ height: '85%' }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
        />

        {/* Section 1: Vehicle Selection */}
        <motion.div
          className="mb-24 md:pl-24"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-end gap-4 mb-10 border-b border-gray-100 pb-4">
            <span className="text-6xl font-black text-gray-100 leading-none -mb-1">{t.steps.step1.number}</span>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-tight">{t.steps.step1.title}</h2>
              <p className="text-gray-500 text-sm">{t.steps.step1.desc}</p>
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-12">
            {/* Sedan */}
            <div>
              <h3 className="text-sm font-bold text-gray-400 mb-6 uppercase tracking-widest pl-1">{t.steps.step1.sedan}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {carModels.sedan.map((car, index) => (
                  <motion.div
                    key={car.id}
                    onClick={() => handleCarSelect(car.id)}
                    className={`group relative bg-gray-50 rounded-xl p-4 cursor-pointer transition-all duration-300 border-2 ${selectedCar === car.id ? 'border-red-600 shadow-xl bg-white scale-105 z-10' : 'border-transparent hover:bg-white hover:shadow-md'}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="aspect-[16/9] mb-3 flex items-center justify-center relative">
                      <div className={`absolute inset-0 bg-red-600/5 rounded-full blur-xl transition-opacity duration-500 ${selectedCar === car.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}></div>
                      <img src={car.image} alt={car.name} className="relative z-10 w-full h-full object-contain transform transition-transform duration-500 group-hover:scale-110" />
                    </div>
                    <div className="flex justify-between items-center px-1">
                      <h4 className={`text-sm font-bold transition-colors ${selectedCar === car.id ? 'text-red-600' : 'text-gray-900'}`}>{car.name}</h4>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center border transition-all ${selectedCar === car.id ? 'bg-red-600 border-red-600 text-white' : 'border-gray-200 text-gray-300 group-hover:border-red-600 group-hover:text-red-600'}`}>
                        {selectedCar === car.id && <Car size={12} />}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* SUV */}
            <div>
              <h3 className="text-sm font-bold text-gray-400 mb-6 uppercase tracking-widest pl-1">{t.steps.step1.suv}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {carModels.suv.map((car, index) => (
                  <motion.div
                    key={car.id}
                    onClick={() => handleCarSelect(car.id)}
                    className={`group relative bg-gray-50 rounded-xl p-4 cursor-pointer transition-all duration-300 border-2 ${selectedCar === car.id ? 'border-red-600 shadow-xl bg-white scale-105 z-10' : 'border-transparent hover:bg-white hover:shadow-md'}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="aspect-[16/9] mb-3 flex items-center justify-center relative">
                      <div className={`absolute inset-0 bg-red-600/5 rounded-full blur-xl transition-opacity duration-500 ${selectedCar === car.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}></div>
                      <img src={car.image} alt={car.name} className="relative z-10 w-full h-full object-contain transform transition-transform duration-500 group-hover:scale-110" />
                    </div>
                    <div className="flex justify-between items-center px-1">
                      <h4 className={`text-sm font-bold transition-colors ${selectedCar === car.id ? 'text-red-600' : 'text-gray-900'}`}>{car.name}</h4>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center border transition-all ${selectedCar === car.id ? 'bg-red-600 border-red-600 text-white' : 'border-gray-200 text-gray-300 group-hover:border-red-600 group-hover:text-red-600'}`}>
                        {selectedCar === car.id && <Car size={12} />}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
          {errors.model_id && <p className="text-red-500 text-sm mt-4 text-center">{errors.model_id.message}</p>}
        </motion.div>

        {/* Section 2: Contact Details */}
        <motion.div
          className={`transition-all duration-700 md:pl-24 ${selectedCar ? 'opacity-100 translate-y-0' : 'opacity-40 translate-y-12 grayscale pointer-events-none'}`}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex items-end gap-4 mb-10 border-b border-gray-100 pb-4">
            <span className={`text-6xl font-black leading-none -mb-1 transition-colors ${selectedCar ? 'text-gray-100' : 'text-gray-50'}`}>{t.steps.step2.number}</span>
            <div>
              <h2 className={`text-2xl font-bold uppercase tracking-tight transition-colors ${selectedCar ? 'text-gray-900' : 'text-gray-300'}`}>{t.steps.step2.title}</h2>
              <p className="text-gray-400 text-sm">{t.steps.step2.desc}</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-3xl p-8 md:p-16">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 max-w-5xl mx-auto">
              {/* Row 1: Title */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900 ml-1">{t.form.title} <span className="text-red-600">*</span></label>
                <div className="w-full md:w-1/4">
                  <Controller
                    control={control}
                    name="title"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} disabled={!selectedCar} value={field.value}>
                        <SelectTrigger className="h-14 bg-white border-0 hover:bg-white focus:ring-0 rounded-xl px-6 text-gray-700 shadow-sm">
                          <SelectValue placeholder={t.form.placeholders.selectTitle} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Mr">Mr</SelectItem>
                          <SelectItem value="Ms">Ms</SelectItem>
                          <SelectItem value="Mrs">Mrs</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
                </div>
              </div>

              {/* Row 2: First Name & Last Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900 ml-1">{t.form.firstName} <span className="text-red-600">*</span></label>
                  <Input
                    {...register("firstName")}
                    className="h-14 bg-white border-0 focus-visible:ring-0 rounded-xl px-6 text-gray-700 shadow-sm"
                    disabled={!selectedCar}
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900 ml-1">{t.form.lastName} <span className="text-red-600">*</span></label>
                  <Input
                    {...register("lastName")}
                    className="h-14 bg-white border-0 focus-visible:ring-0 rounded-xl px-6 text-gray-700 shadow-sm"
                    disabled={!selectedCar}
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>}
                </div>
              </div>

              {/* Row 3: Email Address & Contact Number */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900 ml-1">{t.form.email} <span className="text-red-600">*</span></label>
                  <Input
                    {...register("email")}
                    type="email"
                    className="h-14 bg-white border-0 focus-visible:ring-0 rounded-xl px-6 text-gray-700 shadow-sm"
                    disabled={!selectedCar}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900 ml-1">{t.form.phone} <span className="text-red-600">*</span></label>
                  <div className="flex gap-4">
                    <div className="w-1/3">
                      <Controller
                        control={control}
                        name="countryCode"
                        render={({ field }) => (
                          <Select onValueChange={field.onChange} disabled={!selectedCar} value={field.value}>
                            <SelectTrigger className="h-14 bg-white border-0 hover:bg-white focus:ring-0 rounded-xl px-6 text-gray-700 shadow-sm">
                              <SelectValue placeholder="+968" />
                            </SelectTrigger>
                            <SelectContent>
                              {countries.map((country, index) => (
                                <SelectItem key={index} value={country.phone_prefix}>{country.phone_prefix} ({country.code})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div className="w-2/3">
                      <Input
                        {...register("phone")}
                        placeholder={t.form.placeholders.phone}
                        className="h-14 bg-white border-0 focus-visible:ring-0 rounded-xl px-6 text-gray-700 shadow-sm"
                        disabled={!selectedCar}
                      />
                    </div>
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                </div>
              </div>

              {/* Row 4: Your Location */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900 ml-1">{t.form.location} <span className="text-red-600">*</span></label>
                <Input
                  {...register("location")}
                  className="h-14 bg-white border-0 focus-visible:ring-0 rounded-xl px-6 text-gray-700 shadow-sm"
                  disabled={!selectedCar}
                />
                {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location.message}</p>}
              </div>

              {/* Row 5: Preferred Date & Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900 ml-1">{t.form.date}</label>
                  <Controller
                    control={control}
                    name="date"
                    render={({ field }) => (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "h-14 w-full justify-start text-left font-normal bg-white border-0 hover:bg-white rounded-xl shadow-sm px-6",
                              !field.value && "text-muted-foreground"
                            )}
                            disabled={!selectedCar}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value ? format(field.value, "PPP") : <span>{t.form.placeholders.pickDate}</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                  />
                  {errors.date && <p className="text-red-500 text-xs mt-1">{errors.date.message}</p>}
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-900 ml-1">{t.form.time}</label>
                  <Controller
                    control={control}
                    name="time"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} disabled={!selectedCar} value={field.value}>
                        <SelectTrigger className="h-14 bg-white border-0 hover:bg-white focus:ring-0 rounded-xl px-6 text-gray-700 shadow-sm">
                          <SelectValue placeholder={t.form.placeholders.time} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Anytime">{t.form.timeOptions.anytime}</SelectItem>
                          <SelectItem value="Morning">{t.form.timeOptions.morning}</SelectItem>
                          <SelectItem value="Afternoon">{t.form.timeOptions.afternoon}</SelectItem>
                          <SelectItem value="Evening">{t.form.timeOptions.evening}</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.time && <p className="text-red-500 text-xs mt-1">{errors.time.message}</p>}
                </div>
              </div>

              {/* Row 6: Message */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-900 ml-1">{t.form.message}</label>
                <Textarea
                  {...register("message")}
                  className="min-h-[120px] bg-white border-0 focus-visible:ring-0 rounded-xl px-6 py-4 text-gray-700 shadow-sm resize-none"
                  disabled={!selectedCar}
                />
              </div>

              {/* Footer: Privacy & Checkbox */}
              <div className="flex items-start space-x-3 pt-2">
                <Controller
                  control={control}
                  name="privacyAgreement"
                  render={({ field }) => (
                    <Checkbox
                      id="privacy"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="mt-1"
                      disabled={!selectedCar}
                    />
                  )}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="privacy"
                    className="text-sm font-medium leading-normal text-gray-600 cursor-pointer"
                  >
                    {t.form.privacy}
                  </label>
                  {errors.privacyAgreement && <p className="text-red-500 text-xs">{errors.privacyAgreement.message}</p>}
                </div>
              </div>

              <div className="pt-4 flex flex-col items-center gap-4">
                {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                  <div className="w-full flex flex-col items-center gap-2">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      onChange={handleCaptchaChange}
                    />
                    {errors.recaptcha_token && <p className="text-red-500 text-xs">{errors.recaptcha_token.message}</p>}
                  </div>
                )}
                <Button
                  type="submit"
                  className={`w-full h-14 rounded-xl text-base font-bold transition-all duration-300 ${selectedCar ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
                  disabled={!selectedCar || isSubmitting}
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t.form.submit}
                </Button>
              </div>
            </form>
          </div >
        </motion.div >

      </div >

      {/* Sticky Tab */}
      < div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 hidden xl:flex flex-col gap-2" >
        <div className="bg-gray-900 text-white py-6 px-2 rounded-l-lg writing-vertical-rl text-xs font-bold tracking-widest cursor-pointer hover:bg-red-600 transition-colors shadow-lg">
          {t.bookNow}
        </div>
      </div >
    </div >
  );
};

export default TestDrive;